import * as React from 'react';
import { observer } from 'mobx-react';
import { usePageTitle } from '~components/DashboardWrapper/PageTitle';
import { t } from '~/i18n';
import { Panel, TabsPanel, CreateButton } from '@ixicare/ui';
import { HcpGroupsTable } from '~/components/HcpGroupsTable';
import { HcpGroupCreateEditModal } from '~/containers/HcpGroups/HcpGroupCreateEditModal';
import { HcpAssignmentTab } from '~/containers/HcpGroups/HcpAssignmentTab';
import { ZoneAssignmentTab } from '~/containers/HcpGroups/ZoneAssignmentTab';
import { ZonePresetsTab } from '~/containers/HcpGroups/ZonePresetsTab';
import { ResidentAssignmentTab } from '~/containers/HcpGroups/ResidentAssignmentTab';
import { useSiteConfiguration } from '~containers/ConfigurationForm/ConfigurationForm';
import { useStores } from '~store/utils';

const HcpGroupsPage = observer(() => {
  const { permissions, sites } = useStores();
  usePageTitle(t('menu.dashboard.hcpGroups'));
  const creationPermission = permissions.has('can_create_hcp_groups');
  const editPermission = permissions.has('can_edit_all_hcp_groups');
  const canEditPresets = permissions.has('can_edit_site');
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
  // Seeded from the hash rather than left empty: TabsPanel reports a change only when a tab is
  // clicked, so a link straight to #zonePresets would otherwise land with the wrong tab believed
  // open - and the create button below would offer the wrong thing.
  const [activeTabId, setActiveTabId] = React.useState(() =>
    typeof window === 'undefined' ? '' : window.location.hash.replace('#', '')
  );

  const hcpGroupsTable = React.useMemo(() => {
    return <HcpGroupsTable canCreate={creationPermission} canEdit={editPermission} />;
  }, [creationPermission, editPermission]);

  const tabs = React.useMemo(() => {
    return [
      {
        label: t('hcpGroups.tabs.groups'),
        id: 'groups',
        content: hcpGroupsTable,
      },
      {
        label: t('hcpGroups.tabs.assignHcps'),
        id: 'assignHcps',
        content: <HcpAssignmentTab />,
      },
      {
        label: t('hcpGroups.tabs.assignZones'),
        id: 'assignZones',
        content: <ZoneAssignmentTab />,
      },
      {
        label: t('hcpGroups.tabs.assignResidents.title'),
        id: 'assignResidents',
        content: <ResidentAssignmentTab />,
      },
      ...(canEditPresets
        ? [
            {
              label: t('hcpGroups.tabs.zonePresets'),
              id: 'zonePresets',
              content: <ZonePresetsTab />,
            },
          ]
        : []),
    ];
  }, [hcpGroupsTable, canEditPresets]);

  // The zone presets tab brings its own create button, for presets rather than for groups. Checked
  // against the rendered tabs so a stale hash cannot hide the button on a tab that is not there.
  const onPresetsTab = tabs.some((tab) => tab.id === activeTabId && tab.id === 'zonePresets');

  const { data: siteConfig } = useSiteConfiguration(sites.selectedSite?.uuid ?? null);
  const isSegmentationEnabled = siteConfig?.['enable-segmentation'] ?? false;

  return (
    <>
      {!isSegmentationEnabled && (
        <div
          style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
          }}
        >
          <p style={{ margin: 0 }}>
            {t('hcpGroups.segmentation.disabled.message')}{' '}
            <a href="/configuration" style={{ textDecoration: 'underline', color: '#856404', fontWeight: 'bold' }}>
              {t('hcpGroups.segmentation.disabled.link')}
            </a>
          </p>
        </div>
      )}
      <Panel>
        {editPermission ? <TabsPanel tabs={tabs} useHash={true} onChange={setActiveTabId} /> : hcpGroupsTable}
      </Panel>
      {creationPermission && !onPresetsTab && <CreateButton atBottom onClick={() => setShowCreateModal(true)} />}
      {showCreateModal && <HcpGroupCreateEditModal onClose={() => setShowCreateModal(false)} />}
    </>
  );
});

export default HcpGroupsPage;
